import { ChangeDetectionStrategy, Component, DestroyRef, inject, OnInit, ViewContainerRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule, MatIconRegistry } from '@angular/material/icon';
import { MatListModule } from '@angular/material/list';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatTooltipModule } from '@angular/material/tooltip';
import { DomSanitizer } from '@angular/platform-browser';
import { Router, RouterOutlet } from '@angular/router';

import { CxHeaderModule } from '@bbraun/cortex/header';
import { CxImageModule } from '@bbraun/cortex/image';
import { CxListModule } from '@bbraun/cortex/list';
import { CxNavigationDrawerModule } from '@bbraun/cortex/navigation-drawer';
import { HTTP_STATUS_CODES } from '@bbraun/cortex/number-input';
import { CwHeader, CwSnackbar, CwSplashScreen } from '@bbraun/cortex-angular';
import { AzureInsightsService } from '@bbraun/helpex/src/lib/azure-insights';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import { OAuthService } from 'angular-oauth2-oidc';
import { catchError, concatMap, EMPTY, filter, from, take, tap } from 'rxjs';

import { authCodeFlowConfig } from './auth/auth-code-flow.config';
import { environment } from '../environments/environment';
import { AccessErrorComponent } from './components/access-error/access-error.component';
import {
  AccessMissingPermissionsComponent
} from './components/access-missing-permissions/access-missing-permissions.component';
import { HeaderAppSwitchComponent } from './components/header-app-switch/header-app-switch.component';
import { HeaderProfileComponent } from './components/header-profile/header-profile.component';
import { RepevoNavigationComponent } from './components/rep-evolution/repevo-navigation/repevo-navigation.component';
import { HeaderNotificationsComponent } from './components/replenishment/header-notifications/header-notifications.component';
import { HeaderViewSwitchComponent } from './components/replenishment/header-view-switch/header-view-switch.component';
import { NavigationExternalComponent } from './components/replenishment/navigation-external/navigation-external.component';
import { NavigationInternalComponent } from './components/replenishment/navigation-internal/navigation-internal.component';
import { REP_EVOLUTION, REPLENISHMENT } from './definitions/constants/application.constants';
import { customIconsList } from './definitions/constants/custom-icons';
import { ROUTES } from './definitions/constants/routes.constants';
import { NoAccessReasons } from './definitions/enums/user/no-access-reasons.enum';
import { User } from './definitions/interfaces/user/user';
import { AppLanguageService } from './services/language/app-language.service';
import { UserService } from './services/user/user.service';
import { CountrySwitchSidebarComponent } from './sidebars/switch-country-sidebar/country-switch-sidebar.component';
import {
  SwitchExternalUserSidebarComponent
} from './sidebars/switch-external-user-sidebar/switch-external-user-sidebar.component';
import { SwitchViewSidebarComponent } from './sidebars/switch-view-sidebar/switch-view-sidebar.component';
import { LayoutStoreFacade } from './stores/global-store/layout/layout-store.facade';
import { RouterStoreFacade } from './stores/global-store/router/router-store.facade';
import { UserConfigsStoreFacade } from './stores/global-store/user-configs/user-configs-store.facade';
import { SidebarDisplayStore } from './stores/signal-stores/sidebar-display-store/sidebar-display.store';

@Component({
  selector: 'app-root',
  imports: [
    RouterOutlet,
    CxNavigationDrawerModule,
    CxListModule,
    CxHeaderModule,
    CxImageModule,
    MatButtonModule,
    MatIconModule,
    MatListModule,
    MatExpansionModule,
    NavigationExternalComponent,
    AccessErrorComponent,
    AccessMissingPermissionsComponent,
    NavigationInternalComponent,
    MatSlideToggleModule,
    FormsModule,
    HeaderProfileComponent,
    CountrySwitchSidebarComponent,
    MatTooltipModule,
    SwitchViewSidebarComponent,
    HeaderNotificationsComponent,
    HeaderViewSwitchComponent,
    CwHeader,
    CwSplashScreen,
    TranslocoDirective,
    RepevoNavigationComponent,
    HeaderAppSwitchComponent,
    SwitchExternalUserSidebarComponent
  ],
  templateUrl: './app.component.html',
  styleUrls: [ './app.component.sass' ],
  changeDetection: ChangeDetectionStrategy.Eager
})
export class AppComponent implements OnInit {
  public readonly layoutStoreFacade = inject(LayoutStoreFacade);
  public readonly userConfigsFacade = inject(UserConfigsStoreFacade);
  public readonly sidebarDisplayStore = inject(SidebarDisplayStore);
  public readonly routerFacade = inject(RouterStoreFacade);
  private readonly languageService = inject(AppLanguageService);
  private readonly oAuthService = inject(OAuthService);
  private readonly userService = inject(UserService);
  private readonly router = inject(Router);
  private readonly transloco = inject(TranslocoService);
  private readonly azureInsightsService = inject(AzureInsightsService);
  private readonly vcr = inject(ViewContainerRef);
  private readonly destroyRef = inject(DestroyRef);
  private readonly iconRegistry = inject(MatIconRegistry);
  private readonly sanitizer = inject(DomSanitizer);

  public hasValidToken = false;
  public noAccessReason: NoAccessReasons | undefined;
  public showSplashScreen = true;

  protected readonly ROUTES = ROUTES;
  protected readonly REPLENISHMENT = REPLENISHMENT;
  protected readonly REP_EVOLUTION = REP_EVOLUTION;
  protected readonly NoAccessReasons = NoAccessReasons;
  protected readonly splashScreenText = window.location.pathname === '/logout' ? 'Logging out...' : 'Welcome';

  constructor() {
    customIconsList.forEach(icon => {
      this.iconRegistry.addSvgIcon(
        icon.name,
        this.sanitizer.bypassSecurityTrustResourceUrl(icon.path)
      );
    });

    this.azureInsightsService.handleMonitoring(
      environment.monitoring?.enable,
      environment.monitoring.azureInsightsKey,
      true
    );

    this.azureInsightsService.trackRouterChanges(environment.monitoring?.enable);

    setTimeout(() => this.showSplashScreen = false, 1700);
  }

  public ngOnInit(): void {
    this.initializeSnackbar();
    const language = this.languageService.getCurrent();

    this.transloco.load(language).pipe(take(1)).subscribe();
    this.transloco.setActiveLang(language);

    this.oAuthService.configure({ ...authCodeFlowConfig });
    this.oAuthService.setupAutomaticSilentRefresh();

    from(this.oAuthService.loadDiscoveryDocumentAndLogin())
      .pipe(
        filter(() => this.oAuthService.hasValidAccessToken()),
        tap(() => this.hasValidToken = true),
        concatMap(() => this.userService.getUser().pipe(take(1))),
        tap((user: User) => {
          this.userConfigsFacade.userDataLoaded(user);
          this.router.initialNavigation();
        }),
        catchError((response) => {
          if (response.status === HTTP_STATUS_CODES.FORBIDDEN) {
            this.noAccessReason = NoAccessReasons.MISSING_PERMISSIONS;
            return EMPTY;
          }
          if (response.status === HTTP_STATUS_CODES.BAD_REQUEST && response.error.message.includes('Invalid role relations')) {
            this.noAccessReason = NoAccessReasons.MISSING_PERMISSIONS;
            return EMPTY;
          }
          this.noAccessReason = NoAccessReasons.LOGIN_ERROR;
          return EMPTY;
        }),
        takeUntilDestroyed(this.destroyRef)
      ).subscribe();
  }

  public initializeSnackbar(): void {
    this.vcr.createComponent(CwSnackbar);
  }
}
